import type { Frame } from "@/api/spaces";

type GeometryKey = "position_x" | "position_y" | "width" | "height";
type GeometryUpdate = Partial<Pick<Frame, GeometryKey>>;

const GEOMETRY_KEYS: GeometryKey[] = [
  "position_x",
  "position_y",
  "width",
  "height",
];
const LOCAL_GEOMETRY_TTL_MS = 10_000;

const protectedGeometry = new Map<
  string,
  GeometryUpdate & { expiresAt: number }
>();

function hasGeometry(update: GeometryUpdate): boolean {
  return GEOMETRY_KEYS.some((key) => update[key] !== undefined);
}

export function rememberLocalFrameGeometry(
  frameId: string,
  update: GeometryUpdate,
): void {
  if (!hasGeometry(update)) return;
  protectedGeometry.set(frameId, {
    ...update,
    expiresAt: Date.now() + LOCAL_GEOMETRY_TTL_MS,
  });
}

export function protectLocalFrameGeometry<T extends Partial<Frame>>(
  frameId: string,
  update: T,
): T {
  const expected = protectedGeometry.get(frameId);
  if (!expected) return update;

  if (expected.expiresAt <= Date.now()) {
    protectedGeometry.delete(frameId);
    return update;
  }

  let next: T | null = null;
  for (const key of GEOMETRY_KEYS) {
    const incoming = update[key];
    const wanted = expected[key];
    if (incoming === undefined || wanted === undefined || incoming === wanted) {
      continue;
    }
    next = next ?? { ...update };
    delete next[key];
  }

  return next ?? update;
}

export function clearLocalFrameGeometry(): void {
  protectedGeometry.clear();
}
